/**
 * Cross-platform alert dialog utilities
 *
 * Provides consistent alert/confirm dialogs across web and native platforms
 * Success/error messages use toast notifications
 * Confirmation dialogs use modal alerts (require user interaction)
 * i18n SUPPORTED
 */

import { Platform, Alert } from 'react-native';
import Toast from 'react-native-toast-message';

interface ConfirmOptions {
  confirmText?: string;
  cancelText?: string;
  destructiveAction?: boolean;
}

/**
 * Show a confirmation dialog and return user's choice
 * @param title - Dialog title
 * @param message - Dialog message
 * @param options - Optional configuration for button text and styling
 * @returns Promise that resolves to true if user confirmed, false otherwise
 */
export async function showConfirm(
  title: string,
  message: string,
  options?: ConfirmOptions
): Promise<boolean> {
  const confirmText = options?.confirmText || 'Confirm';
  const cancelText = options?.cancelText || 'Cancel';

  if (Platform.OS === 'web') {
    // Use browser's native confirm on web
    return window.confirm(`${title}\n\n${message}`);
  }

  // Use Alert.alert on native platforms
  return new Promise<boolean>((resolve) => {
    Alert.alert(
      title,
      message,
      [
        {
          text: cancelText,
          style: 'cancel',
          onPress: () => resolve(false),
        },
        {
          text: confirmText,
          style: options?.destructiveAction ? 'destructive' : 'default',
          onPress: () => resolve(true),
        },
      ],
      { cancelable: true }
    );
  });
}

/**
 * Show an alert dialog
 * @param title - Dialog title
 * @param message - Dialog message
 */
export function showAlert(title: string, message: string): void {
  if (Platform.OS === 'web') {
    // Use browser's native alert on web
    window.alert(`${title}\n\n${message}`);
  } else {
    // Use Alert.alert on native platforms
    Alert.alert(title, message);
  }
}

/**
 * Show a success message using toast notification
 * @param message - Success message to display
 */
export function showSuccess(message: string, title?: string): void {
  Toast.show({
    type: 'success',
    text1: title || 'Success',
    text2: message,
    position: 'top',
    visibilityTime: 3000,
    autoHide: true,
    topOffset: 50,
  });
}

/**
 * Show an error message using toast notification
 * @param message - Error message to display
 */
export function showError(message: string, title?: string): void {
  Toast.show({
    type: 'error',
    text1: title || 'Error',
    text2: message,
    position: 'top',
    visibilityTime: 4000,
    autoHide: true,
    topOffset: 50,
  });
}
